import theme from "@config/theme";

type ProgressProps = {
  fraction: number;
  message: string;
  showStatusMonitorToggle?: boolean;
  statusMonitorEnabled?: boolean;
  onStatusMonitorToggle?: () => void;
};

const Progress = ({
  fraction,
  message,
  showStatusMonitorToggle = false,
  statusMonitorEnabled = true,
  onStatusMonitorToggle = () => {},
}: ProgressProps) => {
  const color =
    fraction >= 0.9 ? theme.colors.kellyGreenTint : theme.colors.kellyGreenTint;
  const stripeColor = fraction < 0.9 ? theme.colors.kellyGreenShade : color;
  return (
    <>
      <div className="progress-bar">
        <div className="progress-unfilled" />
      </div>
      <div className="progress-message">
        <span className="message-text">{message}</span>
        {showStatusMonitorToggle && (
          <button
            className={`status-monitor-toggle ${statusMonitorEnabled ? "enabled" : "disabled"}`}
            data-testid="status-monitor-toggle"
            title={
              statusMonitorEnabled
                ? "Disable Status Monitor"
                : "Enable Status Monitor"
            }
            onClick={onStatusMonitorToggle}
            type="button"
          >
            <span className="material-icons-round">
              {statusMonitorEnabled ? "toggle_on" : "toggle_off"}
            </span>
          </button>
        )}
      </div>
      <style jsx>{`
        /*
        // To keep the animated stripes moving at a consistent speed we set
        // the animation on the wider .progress-bar div and then vary the
        // width of the div that represents the _unfilled_ part of the
        // progress bar since that part is not animated (it's just gray like
        // the background).
        */

        .progress-bar {
          width: 100%;
          height: 5px;
          background: ${theme.colors.gray2}
            repeating-linear-gradient(90deg, ${stripeColor}, ${color} 400px);
          background-size: 200% 200%;
          animation: barberpole 5s linear infinite;
          animation-direction: reverse;
        }
        @keyframes barberpole {
          100% {
            background-position: 100% 100%;
          }
        }

        .progress-unfilled {
          width: ${(1 - fraction) * 100}%;
          height: 100%;
          background-color: ${theme.colors.gray2};
          margin-left: auto;
          margin-right: 0;
        }

        .progress-message {
          min-height: ${theme.fonts.size.px28};
          background-color: ${theme.colors.gray2};
          color: ${theme.colors.gray6};
          padding: 3px 19px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .message-text {
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          flex: 1;
        }

        .progress-message:hover .message-text {
          text-overflow: clip;
          white-space: normal;
        }

        .status-monitor-toggle {
          background: none;
          border: none;
          cursor: pointer;
          margin-top: -5px; // Compensate for the height of the progress bar
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: color 0.2s ease;
          flex-shrink: 0;
        }

        .status-monitor-toggle.enabled {
          color: ${theme.colors.ultramarineTint};
        }

        .status-monitor-toggle.disabled {
          color: ${theme.colors.lightGray};
        }

        .status-monitor-toggle:hover {
          background-color: ${theme.colors.gray3};
        }

        .status-monitor-toggle:focus-visible {
          outline: 1px solid ${theme.colors.ultramarineTint};
        }
      `}</style>
    </>
  );
};

export default Progress;
